import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router";
import type { EntityCustomViewParams } from "@rebasepro/cms-types";
import { useSidePanel } from "@rebasepro/cms";
import { useSnackbarController } from "@rebasepro/app";
import {
    Alert,
    BooleanSwitchWithLabel,
    Button,
    Card,
    Chip,
    cls,
    defaultBorderMixin,
    LoadingButton,
    Select,
    SelectItem,
    TextField,
    Typography
} from "@rebasepro/ui";
import { entityPath } from "rebase-leadgen-config";
import { describeError, fullStamp, inboxApi, sinceLabel, type MailboxSummary } from "../inbox/shared";
import { client } from "../lib/rebaseClient";

/**
 * One mailbox — added, checked and fixed here.
 *
 * Replaces the field-by-field record form on `mailboxes`. A new mailbox is a
 * short setup: which provider, which address, the app password. The password
 * never passes through the record form at all — it goes to the endpoint that
 * seals it, because the row only ever holds it encrypted, and a form field
 * bound to that column would show ciphertext and invite somebody to edit it.
 *
 * An existing mailbox opens on how it is doing — read when, failing why — with
 * the buttons that answer "is it working?" without waiting fifteen minutes for
 * the next read: test the login both ways, read it now, read it again from
 * scratch. The settings below are held here and saved by their own button,
 * straight to the row.
 *
 * Not through the form context. In the admin this app runs, a `formView`
 * Builder is rendered INSTEAD of the record form, so the context it gets is the
 * framework's read-only stand-in, whose `setFieldValue` throws — every keystroke
 * in Settings was thrown away, and there was no Save button to press anyway.
 */

type Row = Record<string, unknown>;
type Provider = "gmail" | "fastmail" | "icloud" | "custom";

const PROVIDERS: { id: Provider; label: string; help: string }[] = [
    { id: "gmail", label: "Google / Gmail", help: "Use an App Password (myaccount.google.com/apppasswords) — Google refuses the account password over IMAP. It needs 2-Step Verification switched on first, and on Workspace an admin must allow app passwords and IMAP." },
    { id: "fastmail", label: "Fastmail", help: "Use an app password: Settings → Privacy & Security → Integrations → App passwords." },
    { id: "icloud", label: "iCloud", help: "Use an app-specific password from account.apple.com → Sign-In and Security." },
    { id: "custom", label: "Other (IMAP/SMTP)", help: "The servers and the password your provider gives for mail clients." }
];

const COLORS = ["blue", "green", "purple", "orange", "teal", "pink"];
const STATUS: Record<string, { label: string; color: string }> = {
    new: { label: "Not read yet", color: "grayLighter" },
    ok: { label: "Working", color: "greenLighter" },
    needs_password: { label: "Needs a password", color: "orangeLighter" },
    error: { label: "Failing", color: "redLighter" },
    off: { label: "Switched off", color: "grayLighter" }
};

const str = (value: unknown) => (typeof value === "string" ? value : value == null ? "" : String(value));
const splitList = (value: string) => value.split(/[,;\s]+/).map(v => v.trim()).filter(Boolean);

export function MailboxView({ entity, formContext }: EntityCustomViewParams) {
    const id = entity?.id != null ? String(entity.id) : "";
    return id
        ? <ExistingMailbox id={id} source={(formContext?.values ?? entity?.values) as Row | undefined} disabled={Boolean(formContext?.disabled)} />
        : <NewMailbox openEntityMode={formContext?.openEntityMode} />;
}

/** What Settings may write. Never the password, which has its own endpoint. */
const SETTINGS = [
    "name", "address", "from_name", "username", "aliases", "provider", "color",
    "imap_host", "imap_port", "smtp_host", "smtp_port",
    "inbox_folder", "sent_folder", "backfill_days",
    "sync_enabled", "send_enabled", "outreach_default", "signature"
] as const;
const NUMBERS = new Set<string>(["imap_port", "smtp_port", "backfill_days"]);
const TRIMMED = new Set<string>(["name", "address", "from_name", "username", "imap_host", "smtp_host", "inbox_folder", "sent_folder"]);
const REQUIRED: Record<string, string> = { name: "Name in the inbox", address: "Address", username: "Login" };

/**
 * A setting as the row stores it. Fields are edited as the text typed —
 * splitting the alias list or parsing a port on every keystroke eats the comma
 * or the digit being typed — and turned into the column's shape here.
 */
function asStored(key: string, value: unknown): unknown {
    if (key === "aliases") return Array.isArray(value) ? value : splitList(str(value));
    if (NUMBERS.has(key)) {
        const typed = str(value).trim();
        const number = Number(typed);
        return typed === "" || !Number.isFinite(number) ? null : number;
    }
    if (key === "sent_folder") return str(value).trim() || null;
    if (TRIMMED.has(key)) return str(value).trim();
    return value ?? null;
}

// ── Adding one ───────────────────────────────────────────────────────────

function NewMailbox({ openEntityMode }: { openEntityMode?: string }) {
    const navigate = useNavigate();
    const sidePanel = useSidePanel();
    const snackbar = useSnackbarController();
    const [provider, setProvider] = useState<Provider>("gmail");
    const [address, setAddress] = useState("");
    const [name, setName] = useState("");
    const [fromName, setFromName] = useState("");
    const [username, setUsername] = useState("");
    const [password, setPassword] = useState("");
    const [aliases, setAliases] = useState("");
    const [imapHost, setImapHost] = useState("");
    const [smtpHost, setSmtpHost] = useState("");
    const [smtpPort, setSmtpPort] = useState("465");
    const [outreach, setOutreach] = useState(false);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState<string | undefined>();
    const help = PROVIDERS.find(p => p.id === provider)?.help;

    const create = async () => {
        setSaving(true);
        setError(undefined);
        try {
            const { mailbox } = await inboxApi.createMailbox({
                provider, address: address.trim(), name: name.trim() || undefined, from_name: fromName.trim() || undefined,
                username: username.trim() || undefined, password, aliases: splitList(aliases), outreach_default: outreach,
                ...(provider === "custom" ? { imap_host: imapHost.trim(), smtp_host: smtpHost.trim(), smtp_port: Number(smtpPort) || 465, smtp_secure: Number(smtpPort) === 465 } : {})
            });
            snackbar.open({ type: "success", message: `${mailbox.address} added — it is read within a minute, or press “Read now”.` });
            // Straight into the mailbox itself, where "Test connection" is.
            if (openEntityMode === "side_panel") sidePanel.replace({ path: "mailboxes", entityId: mailbox.id });
            else navigate(entityPath("mailboxes", mailbox.id));
        } catch (err) {
            setError(describeError(err));
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="p-6 max-w-2xl flex flex-col gap-5">
            <div>
                <Typography variant="h5">Add a mailbox</Typography>
                <Typography variant="body2" color="secondary">Its mail is read into the inbox, and replies to it go out from it.</Typography>
            </div>
            <Card className={cls("p-4 flex flex-col gap-3", defaultBorderMixin)}>
                <Select size="small" label="Provider" value={provider} onValueChange={value => setProvider(value as Provider)} fullWidth
                    renderValue={value => PROVIDERS.find(p => p.id === value)?.label ?? String(value)}>
                    {PROVIDERS.map(p => <SelectItem key={p.id} value={p.id}>{p.label}</SelectItem>)}
                </Select>
                <div className="grid grid-cols-12 gap-3">
                    <div className="col-span-12 md:col-span-6">
                        <TextField size="small" type="email" label="Address" placeholder="hello@yourcompany.com" value={address} onChange={e => setAddress(e.target.value)} />
                    </div>
                    <div className="col-span-12 md:col-span-6">
                        <TextField size="small" label="Name in the inbox (optional)" placeholder="hello@" value={name} onChange={e => setName(e.target.value)} />
                    </div>
                    <div className="col-span-12 md:col-span-6">
                        <TextField size="small" label="Sender name (optional)" placeholder="Francesco" value={fromName} onChange={e => setFromName(e.target.value)} />
                    </div>
                    <div className="col-span-12 md:col-span-6">
                        <TextField size="small" label="Login (if not the address)" placeholder="for an alias: the account it belongs to" value={username} onChange={e => setUsername(e.target.value)} />
                    </div>
                </div>
                <TextField size="small" type="password" label="App password" value={password} onChange={e => setPassword(e.target.value)} autoComplete="new-password" />
                {help && <Typography variant="caption" color="secondary">{help}</Typography>}
                <TextField size="small" label="Also receives as (optional)" placeholder="other addresses that deliver here, comma-separated" value={aliases} onChange={e => setAliases(e.target.value)} />
                {provider === "custom" && (
                    <div className="grid grid-cols-12 gap-3">
                        <div className="col-span-12 md:col-span-6"><TextField size="small" label="IMAP server" placeholder="imap.example.com" value={imapHost} onChange={e => setImapHost(e.target.value)} /></div>
                        <div className="col-span-8 md:col-span-4"><TextField size="small" label="SMTP server" placeholder="smtp.example.com" value={smtpHost} onChange={e => setSmtpHost(e.target.value)} /></div>
                        <div className="col-span-4 md:col-span-2"><TextField size="small" label="SMTP port" value={smtpPort} onChange={e => setSmtpPort(e.target.value)} /></div>
                    </div>
                )}
                <BooleanSwitchWithLabel size="small" value={outreach} onValueChange={setOutreach} label="Cold outreach goes out from this mailbox" />
                {error && <Alert color="error" size="small">{error}</Alert>}
                <div>
                    <LoadingButton variant="filled" color="primary" loading={saving} disabled={!address.includes("@") || !password} onClick={() => void create()}>
                        Add mailbox
                    </LoadingButton>
                </div>
            </Card>
        </div>
    );
}

// ── Looking after one ────────────────────────────────────────────────────

function ExistingMailbox({ id, source, disabled }: { id: string; source: Row | undefined; disabled: boolean }) {
    const snackbar = useSnackbarController();
    // `saved` is the row as last read or written; `values` is what is on
    // screen. Settings are dirty where the two differ.
    const [saved, setSaved] = useState<Row>(() => source ?? {});
    const [values, setValues] = useState<Row>(() => source ?? {});
    const changed = SETTINGS.filter(key =>
        JSON.stringify(asStored(key, values[key]) ?? null) !== JSON.stringify(asStored(key, saved[key]) ?? null));
    const dirty = changed.length > 0;
    useEffect(() => {
        // The row changed underneath: follow it, unless somebody is mid-edit.
        if (!source) return;
        setSaved(source);
        if (!dirty) setValues(source);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [source]);
    const [summary, setSummary] = useState<MailboxSummary | undefined>();
    const [password, setPassword] = useState("");
    const [busy, setBusy] = useState<string | undefined>();
    const [test, setTest] = useState<{ imap: { ok: boolean; error?: string; folder?: string; sentFolder?: string; messages?: number }; smtp: { ok: boolean; error?: string } } | undefined>();
    const [error, setError] = useState<string | undefined>();

    const refresh = async () => {
        const { mailboxes } = await inboxApi.mailboxes();
        setSummary(mailboxes.find(m => m.id === id));
    };
    useEffect(() => { void refresh().catch(err => setError(describeError(err))); }, [id]);

    const set = (key: string, value: unknown) => setValues(current => ({ ...current, [key]: value }));
    const run = async (what: string, action: () => Promise<unknown>) => {
        setBusy(what);
        setError(undefined);
        try { await action(); } catch (err) { setError(describeError(err)); } finally { setBusy(undefined); }
    };

    const saveSettings = () => run("settings", async () => {
        const patch = Object.fromEntries(changed.map(key => [key, asStored(key, values[key])]));
        const missing = Object.keys(REQUIRED).filter(key => key in patch && !patch[key]);
        if (missing.length) throw new Error(`${missing.map(key => REQUIRED[key]).join(" and ")} cannot be empty.`);
        await client.data.collection("mailboxes").update(id, patch);
        setSaved(current => ({ ...current, ...patch }));
        setValues(current => ({ ...current, ...patch }));
        snackbar.open({ type: "success", message: "Saved. Press “Test connection” to check it." });
        await refresh();
    });

    const status = STATUS[summary?.status ?? str(values.status)] ?? STATUS.new;
    const provider = (str(values.provider) || "gmail") as Provider;
    const help = PROVIDERS.find(p => p.id === provider)?.help;

    return (
        <div className="p-6 max-w-3xl flex flex-col gap-5">
            <div className="flex items-center gap-3 flex-wrap">
                <Typography variant="h5">{str(saved.name) || str(saved.address)}</Typography>
                <Chip size="small" colorScheme={status.color as never}>{status.label}</Chip>
            </div>

            {/* How it is doing */}
            <Card className={cls("p-4 flex flex-col gap-3", defaultBorderMixin)}>
                <Typography variant="body2" color="secondary">
                    {str(saved.address)}
                    {summary?.lastSyncedAt ? ` · read ${sinceLabel(summary.lastSyncedAt)} (${fullStamp(summary.lastSyncedAt)})` : " · not read yet"}
                </Typography>
                {summary?.lastError && <Alert color="error" size="small">The last read failed: {summary.lastError}</Alert>}
                {summary?.needsAttention && <Alert color="warning" size="small">{summary.needsAttention}</Alert>}
                {summary && !summary.canRead && !summary.lastError && <Alert color="warning" size="small">It cannot be read yet: {summary.readProblems.join(" ")}</Alert>}
                {summary && summary.canRead && !summary.canSend && <Alert color="warning" size="small">It can be read but not sent from: {summary.sendProblems.join(" ")}</Alert>}

                {test && (
                    <div className="flex flex-col gap-1">
                        <Typography variant="body2" color={test.imap.ok ? "primary" : "error"}>
                            Reading (IMAP): {test.imap.ok ? `works — ${test.imap.messages ?? 0} messages in ${test.imap.folder}${test.imap.sentFolder ? `, sent mail in “${test.imap.sentFolder}”` : ", no Sent folder found"}` : test.imap.error}
                        </Typography>
                        <Typography variant="body2" color={test.smtp.ok ? "primary" : "error"}>
                            Sending (SMTP): {test.smtp.ok ? "works — the login was accepted (nothing was sent)" : test.smtp.error}
                        </Typography>
                    </div>
                )}

                <div className="flex gap-2 flex-wrap">
                    <LoadingButton size="small" variant="outlined" loading={busy === "test"} disabled={Boolean(busy)} onClick={() => void run("test", async () => setTest(await inboxApi.testMailbox(id)))}>
                        Test connection
                    </LoadingButton>
                    <LoadingButton size="small" variant="outlined" loading={busy === "sync"} disabled={Boolean(busy)} onClick={() => void run("sync", async () => {
                        const result = await inboxApi.sync({ force: true, mailbox: id });
                        snackbar.open({ type: result?.success === false ? "warning" : "success", message: result?.success === false ? "It could not be read — see above." : `${Number(result?.kept ?? 0)} new message(s)` });
                        await refresh();
                    })}>
                        Read now
                    </LoadingButton>
                    <Button size="small" variant="text" disabled={Boolean(busy)} onClick={() => {
                        if (!window.confirm("Read this mailbox again from the start of its window? Messages already here are recognised and not duplicated. The mailbox itself is not touched.")) return;
                        void run("reset", async () => {
                            await inboxApi.resetMailbox(id);
                            snackbar.open({ type: "info", message: "It will be read again from the start of its window at the next read." });
                            await refresh();
                        });
                    }}>
                        Read again from scratch
                    </Button>
                </div>
            </Card>

            {/* The password, which never touches the form */}
            <Card className={cls("p-4 flex flex-col gap-3", defaultBorderMixin)}>
                <Typography variant="subtitle2">Password</Typography>
                <Typography variant="body2" color="secondary">
                    {summary?.passwordSet ? `Set${values.password_set_at ? ` ${sinceLabel(str(values.password_set_at))}` : ""} — stored encrypted, never shown.` : "Not set. Nothing can be read or sent until it is."}
                </Typography>
                <div className="flex gap-2 items-end flex-wrap">
                    <TextField size="small" type="password" label={summary?.passwordSet ? "New password" : "App password"} value={password} onChange={e => setPassword(e.target.value)} className="grow" autoComplete="new-password" />
                    <LoadingButton size="small" variant="outlined" loading={busy === "password"} disabled={!password || Boolean(busy)} onClick={() => void run("password", async () => {
                        await inboxApi.setPassword(id, password);
                        setPassword("");
                        snackbar.open({ type: "success", message: "Password saved. Press “Test connection” to check it." });
                        await refresh();
                    })}>
                        Save password
                    </LoadingButton>
                </div>
                {help && <Typography variant="caption" color="secondary">{help}</Typography>}
            </Card>

            {error && <Alert color="error" size="small">{error}</Alert>}

            {/* Settings — held here, saved by the button under them */}
            <Card className={cls("p-4 flex flex-col gap-3", defaultBorderMixin)}>
                <Typography variant="subtitle2">Settings</Typography>
                <div className="grid grid-cols-12 gap-3">
                    <div className="col-span-12 md:col-span-6"><TextField size="small" label="Name in the inbox" value={str(values.name)} disabled={disabled} onChange={e => set("name", e.target.value)} /></div>
                    <div className="col-span-12 md:col-span-6"><TextField size="small" label="Address" value={str(values.address)} disabled={disabled} onChange={e => set("address", e.target.value)} /></div>
                    <div className="col-span-12 md:col-span-6"><TextField size="small" label="Sender name" value={str(values.from_name)} disabled={disabled} onChange={e => set("from_name", e.target.value)} /></div>
                    <div className="col-span-12 md:col-span-6"><TextField size="small" label="Login — for an alias, the account it belongs to" value={str(values.username)} disabled={disabled} onChange={e => set("username", e.target.value)} /></div>
                    <div className="col-span-12">
                        <TextField size="small" label="Also receives as" value={Array.isArray(values.aliases) ? (values.aliases as string[]).join(", ") : str(values.aliases)} disabled={disabled} onChange={e => set("aliases", e.target.value)} />
                    </div>
                    <div className="col-span-12 md:col-span-6">
                        <Select size="small" label="Provider" value={provider} disabled={disabled} onValueChange={value => set("provider", value)} fullWidth renderValue={value => PROVIDERS.find(p => p.id === value)?.label ?? String(value)}>
                            {PROVIDERS.map(p => <SelectItem key={p.id} value={p.id}>{p.label}</SelectItem>)}
                        </Select>
                    </div>
                    <div className="col-span-12 md:col-span-6">
                        <Select size="small" label="Colour in the inbox" value={str(values.color) || "blue"} disabled={disabled} onValueChange={value => set("color", value)} fullWidth>
                            {COLORS.map(color => <SelectItem key={color} value={color}>{color}</SelectItem>)}
                        </Select>
                    </div>
                    {provider === "custom" && (
                        <>
                            <div className="col-span-8"><TextField size="small" label="IMAP server" value={str(values.imap_host)} disabled={disabled} onChange={e => set("imap_host", e.target.value)} /></div>
                            <div className="col-span-4"><TextField size="small" label="IMAP port" value={str(values.imap_port)} disabled={disabled} onChange={e => set("imap_port", e.target.value)} /></div>
                            <div className="col-span-8"><TextField size="small" label="SMTP server" value={str(values.smtp_host)} disabled={disabled} onChange={e => set("smtp_host", e.target.value)} /></div>
                            <div className="col-span-4"><TextField size="small" label="SMTP port" value={str(values.smtp_port)} disabled={disabled} onChange={e => set("smtp_port", e.target.value)} /></div>
                        </>
                    )}
                    <div className="col-span-12 md:col-span-4"><TextField size="small" label="Inbox folder" value={str(values.inbox_folder) || "INBOX"} disabled={disabled} onChange={e => set("inbox_folder", e.target.value)} /></div>
                    <div className="col-span-12 md:col-span-4"><TextField size="small" label="Sent folder (blank: find it)" value={str(values.sent_folder)} disabled={disabled} onChange={e => set("sent_folder", e.target.value)} /></div>
                    <div className="col-span-12 md:col-span-4"><TextField size="small" label="First read goes back (days)" value={str(values.backfill_days)} disabled={disabled} onChange={e => set("backfill_days", e.target.value)} /></div>
                </div>
                <BooleanSwitchWithLabel size="small" value={values.sync_enabled !== false} disabled={disabled} onValueChange={v => set("sync_enabled", v)} label="Read its mail into the inbox" />
                <BooleanSwitchWithLabel size="small" value={values.send_enabled !== false} disabled={disabled} onValueChange={v => set("send_enabled", v)} label="Send from it" />
                <BooleanSwitchWithLabel size="small" value={values.outreach_default === true} disabled={disabled} onValueChange={v => set("outreach_default", v)} label="Cold outreach goes out from here (keep it off the address your customers use)" />
                <TextField size="small" multiline minRows={3} label="Signature — added under replies and new messages" value={str(values.signature)} disabled={disabled} onChange={e => set("signature", e.target.value)} />
                <div className="flex gap-2 items-center flex-wrap">
                    <LoadingButton size="small" variant="filled" color="primary" loading={busy === "settings"} disabled={disabled || !dirty || Boolean(busy)} onClick={() => void saveSettings()}>
                        Save settings
                    </LoadingButton>
                    {dirty && <Button size="small" variant="text" disabled={Boolean(busy)} onClick={() => setValues(saved)}>Discard changes</Button>}
                    {dirty && <Typography variant="caption" color="secondary">Not saved yet.</Typography>}
                </div>
            </Card>
        </div>
    );
}
